// useProducts Hook - Fetch products from API with pagination
// Filtering and sorting is handled server-side for proper pagination

import { useState, useEffect, useCallback } from 'react';
import { Product } from '../types/Product';
import { 
  getAllProducts, 
  getProductsByCategory, 
  searchProducts, 
  ApiProduct, 
  PaginationInfo,
  AvailabilityFilter 
} from '../services/productService';

// Re-export for convenience
export type { AvailabilityFilter } from '../services/productService';

interface UseProductsOptions {
  category?: string;
  search?: string;
  page?: number;
  limit?: number;
  tags?: string[];
  availability?: AvailabilityFilter;
}

interface UseProductsReturn {
  products: Product[];
  pagination: PaginationInfo | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

// Map API product to frontend Product type
function mapApiProduct(apiProduct: ApiProduct): Product {
  return {
    id: apiProduct.productId,
    name: apiProduct.name,
    description: apiProduct.description,
    price: apiProduct.price,
    image: apiProduct.image,
    category: apiProduct.category,
    inventory: apiProduct.inventory,
    tags: apiProduct.tags
  };
}

export function useProducts(options: UseProductsOptions = {}): UseProductsReturn {
  const [products, setProducts] = useState<Product[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchTrigger, setFetchTrigger] = useState(0);

  const { 
    category, 
    search, 
    page = 1, 
    limit = 12, 
    tags = [],
    availability = 'all'
  } = options;

  // Memoize tags array to prevent infinite loops - use JSON.stringify for stable comparison
  const tagsKey = JSON.stringify(tags);

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      let response;
      
      // Parse tags back from the key
      const parsedTags: string[] = JSON.parse(tagsKey);
      
      // Priority: search > category > all
      // All functions now accept availability parameter
      if (search) {
        response = await searchProducts(search, page, limit, parsedTags, availability);
      } else if (category) {
        response = await getProductsByCategory(category, page, limit, parsedTags, availability);
      } else {
        response = await getAllProducts(page, limit, parsedTags, availability);
      }
      
      // Map API products to frontend Product type
      const mappedProducts: Product[] = response.products.map(mapApiProduct);
      
      setProducts(mappedProducts);
      setPagination(response.pagination);
      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load products');
      setLoading(false);
    }
  }, [category, search, page, limit, tagsKey, availability]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts, fetchTrigger]);

  const refetch = useCallback(() => {
    setFetchTrigger(prev => prev + 1);
  }, []);

  return { products, pagination, loading, error, refetch };
}

export default useProducts;